import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import VideoLibraryOutlined from "@mui/icons-material/VideoLibraryOutlined";
import { GlobalContext } from "../../../context/GlobalContext";
import { useAssetLists } from "../../videoStudio/clipping/hooks/useAssetLists";
import { useClippingJob } from "../../videoStudio/clipping/hooks/useClippingJob";
import ClippingForm, { defaultClippingForm } from "../../videoStudio/clipping/components/ClippingForm";
import ClippingJobCard from "../../videoStudio/clipping/components/ClippingJobCard";
import ClipResultGrid from "../../videoStudio/clipping/components/ClipResultGrid";
import ClippingFaqAccordion from "./ClippingFaqAccordion";
import ShareClipModal from "./ShareClipModal";

export default function CreateClipView({ lang, onViewLibrary }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const assets = useAssetLists(hitAxios);
  const job = useClippingJob(hitAxios, {
    onStarted: () => {
      showSnack?.(
        lang?.backgroundJobClippingStarted ||
          "Clipping runs in the background — you can leave this page. We'll notify you when clips are ready.",
        true,
      );
    },
  });
  const [shareClip, setShareClip] = React.useState(null);

  const handleSubmit = async (payload) => {
    await job.submit(payload);
  };

  const handleCopyCaption = (text) => {
    const value = String(text || "").trim();
    if (!value) return;
    navigator.clipboard?.writeText(value).then(() => {
      showSnack?.(lang?.clippingCaptionCopied || "Caption copied.", true);
    });
  };

  const resultForGrid = React.useMemo(() => {
    if (!job.result) return null;
    const r = job.result;
    if (r.project) {
      return {
        shorts: r.shorts || r.project?.clips || [],
        title: r.project.source_title,
        url: r.project.source_url,
        project: r.project,
      };
    }
    return r;
  }, [job.result]);

  if (assets.loading) {
    return (
      <Stack alignItems="center" py={6} spacing={2}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          {lang?.clippingLoadingAssets || "Loading templates and assets…"}
        </Typography>
      </Stack>
    );
  }

  if (assets.error) {
    return <Alert severity="error">{assets.error}</Alert>;
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={8}>
        <Card variant="outlined" data-tour="ai-clipping-form" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2.5}>
              {job.phase === "failed" && job.error && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={job.reset}>
                      {lang?.clippingDismiss || "Dismiss"}
                    </Button>
                  }
                >
                  {job.error}
                </Alert>
              )}

              {job.phase === "complete" && resultForGrid && (
                <>
                  <ClipResultGrid
                    result={resultForGrid}
                    onCopyCaption={handleCopyCaption}
                    onShareClip={setShareClip}
                    lang={lang}
                  />
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="outlined"
                      startIcon={<RefreshOutlined />}
                      onClick={job.reset}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                      {lang?.clippingNewJob || "New clipping job"}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<VideoLibraryOutlined />}
                      onClick={onViewLibrary}
                      sx={{ borderRadius: 2, fontWeight: 700 }}
                    >
                      {lang?.aiClippingViewLibrary || "View in My Clips"}
                    </Button>
                  </Stack>
                </>
              )}

              {(job.phase === "polling" || job.phase === "submitting" || job.phase === "fetching") && (
                <>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    {lang?.backgroundJobClippingHint ||
                      "This job runs in the background. Browse other pages — we'll update you when clips are ready."}
                  </Alert>
                  <ClippingJobCard
                    job={job}
                    onCancel={job.cancel}
                    fetching={job.phase === "fetching"}
                    lang={lang}
                  />
                </>
              )}

              {job.phase !== "complete" && job.phase !== "polling" && job.phase !== "fetching" && (
                <ClippingForm
                  assets={assets}
                  initialForm={defaultClippingForm(assets)}
                  disabled={job.phase === "submitting"}
                  onSubmit={handleSubmit}
                  lang={lang}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} lg={4}>
        <ClippingFaqAccordion lang={lang} />
      </Grid>

      <ShareClipModal
        open={Boolean(shareClip)}
        onClose={() => setShareClip(null)}
        clip={shareClip}
        lang={lang}
      />
    </Grid>
  );
}
